/**
 * ADR-197 — date-only values are calendar dates, not instants.
 *
 * Radar stores start/end/hire/resign/effective dates as Postgres `date`
 * (`@db.Date`). The API sends them as UTC midnight
 * (`"2026-10-30T00:00:00.000Z"`) or as `'YYYY-MM-DD'`. Parsing that into the
 * viewer's local time (`new Date(v).toLocaleDateString()`, `dayjs(v).format()`)
 * shows the previous day to everyone west of UTC — and a picker pre-filled that
 * way writes the previous day back on save.
 *
 * Rule: a date-only value is reduced to its calendar date (year, month, day)
 * with `toCalendarDate` and never read as a local instant. Instants
 * (`createdAt`, `updatedAt`, run timestamps, meeting times) are NOT date-only
 * and keep local-time formatting — do not route them through this module.
 *
 * Accepted inputs everywhere below:
 *  - 'YYYY-MM-DD…' strings → that date (any time part is ignored)
 *  - a Date at exact UTC midnight (how Prisma returns `@db.Date`) → its UTC date
 *  - any other Date (e.g. a picker's local midnight) → its local date
 *  - a dayjs value (picker output) → its local calendar fields
 */
import dayjs from 'dayjs';
import { toCalendarDate } from './calendarDuration';

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * @param {string|Date|import('dayjs').Dayjs|null|undefined} value
 * @returns {string|null} 'YYYY-MM-DD', or null when empty or unparseable
 */
export const toYmd = (value) => {
  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value.format('YYYY-MM-DD') : null;
  }
  const date = toCalendarDate(value);
  if (!date) {
    return null;
  }
  return `${date.year}-${pad2(date.month + 1)}-${pad2(date.day)}`;
};

/**
 * Date-only value → a Date at LOCAL midnight of that calendar day, or null.
 * For call sites that format with `toLocaleDateString(...)`: swapping
 * `new Date(v)` for `toLocalDate(v)` keeps their output format and fixes the day.
 * @param {string|Date|import('dayjs').Dayjs|null|undefined} value
 * @returns {Date|null}
 */
export const toLocalDate = (value) => {
  const ymd = toYmd(value);
  if (!ymd) {
    return null;
  }
  const [year, month, day] = ymd.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Formats a date-only value without ever reading it as a local instant.
 * @param {string|Date|import('dayjs').Dayjs|null|undefined} value
 * @param {string} [pattern='DD/MM/YYYY'] - any dayjs format pattern
 * @param {string} [empty=''] - returned for an empty or unparseable value
 * @returns {string}
 */
export const formatDateOnly = (value, pattern = 'DD/MM/YYYY', empty = '') => {
  const ymd = toYmd(value);
  if (!ymd) {
    return empty;
  }
  // 'YYYY-MM-DD' parses as LOCAL midnight of that day, so local formatting
  // prints exactly that calendar date in every time zone.
  return dayjs(ymd).format(pattern);
};

/**
 * Date-only value → the dayjs value an MUI DatePicker expects (local midnight
 * of that calendar day), or null.
 * @param {string|Date|import('dayjs').Dayjs|null|undefined} value
 * @returns {import('dayjs').Dayjs|null}
 */
export const toPickerValue = (value) => {
  const ymd = toYmd(value);
  return ymd ? dayjs(ymd) : null;
};

/**
 * Date-only value → dayjs at LOCAL midnight of that calendar day, or null — for
 * day comparisons against "today" (`toLocalDay(v)?.isBefore(dayjs(), 'day')`).
 * Same value as toPickerValue, named for use outside pickers.
 */
export const toLocalDay = toPickerValue;

/**
 * DatePicker value → 'YYYY-MM-DD' for the API, or null. Reads the picker's
 * local calendar fields, which are the day the user chose.
 * @param {import('dayjs').Dayjs|null|undefined} value
 * @returns {string|null}
 */
export const fromPickerValue = (value) => toYmd(value);

/**
 * True when two date-only values are the same calendar day (both empty counts
 * as the same).
 */
export const isSameDateOnly = (a, b) => toYmd(a) === toYmd(b);

/**
 * Edit-form initial state for a history entry with a start and an optional end
 * (role, grade, position, manager history).
 * @param {{startDate?: any, endDate?: any}|null|undefined} entry
 * @returns {{startDate: import('dayjs').Dayjs|null, endDate: import('dayjs').Dayjs|null}}
 */
export const toEditFormDates = (entry) => ({
  startDate: toPickerValue(entry?.startDate),
  endDate: toPickerValue(entry?.endDate)
});

/**
 * The date part of an edit payload for a history entry, keeping the existing
 * contract of the role/grade/position update endpoints: `startDate` when set,
 * `endDate` when set, `endDate: null` when an existing end was cleared.
 * @param {{startDate?: any, endDate?: any}} form - picker values
 * @param {{endDate?: any}} entry - the entry being edited
 * @returns {{startDate?: string, endDate?: string|null}}
 */
export const toEditedDatesPayload = (form, entry) => {
  const payload = {};
  const startDate = fromPickerValue(form?.startDate);
  const endDate = fromPickerValue(form?.endDate);
  if (startDate) {
    payload.startDate = startDate;
  }
  if (endDate) {
    payload.endDate = endDate;
  } else if (entry?.endDate) {
    payload.endDate = null;
  }
  return payload;
};
